/**
 * Custom hook to manage timed progress tracking with status updates.
 *
 * This hook simulates progress over time and synchronizes with API status calls.
 * It starts at 0%, jumps to 30% at the first API call, 60% at the second, and
 * linearly interpolates to 97% until completion is detected.
 *
 * @param {number} startTime - Timestamp when the process started (used as dependency to reset)
 * @param {number} totalDurationMs - Total expected duration in milliseconds for each progress segment
 * @param {Function} getProgressValueAndStatusApiCallCount - Function that returns an object with:
 *   - progressIndicatorValue: Current progress percentage (0, 33, 66, or 100)
 *   - statusApiTriggeredCount: Number of API calls made (used to determine progress stage)
 * @param {boolean} showProgress - Flag to enable/disable progress tracking
 *
 * @returns {number} Current progress value (0-100)
 *
 * @example
 * const { progressIndicatorValue, statusApiTriggeredCount } = getStatus()
 * const progress = useTimedProgress(
 *   Date.now(),
 *   60000,
 *   () => ({ progressIndicatorValue, statusApiTriggeredCount }),
 *   true
 * )
 */

import { useEffect, useRef, useState } from 'react'

export default function useTimedProgress(
  startTime,
  totalDurationMs,
  getProgressValueAndStatusApiCallCount,
  showProgress
) {
  const [progress, setProgress] = useState(0)

  const baseProgressRef = useRef(0)
  const remainingMsRef = useRef(totalDurationMs)
  const baselineTimeRef = useRef(Date.now())

  useEffect(() => {
    if (showProgress) {
      baseProgressRef.current = 0
      remainingMsRef.current = totalDurationMs
      baselineTimeRef.current = Date.now()
      setProgress(0)
    }
  }, [startTime, totalDurationMs, showProgress])

  useEffect(() => {
    if (!showProgress) return

    const { progressIndicatorValue, statusApiTriggeredCount } =
      getProgressValueAndStatusApiCallCount()

    if (progressIndicatorValue === 100) {
      setProgress(100)
      return
    }

    if (progressIndicatorValue > 0 && statusApiTriggeredCount === 1) {
      if (progressIndicatorValue === 33) {
        baseProgressRef.current = 30
        remainingMsRef.current = 7 * 60 * 1000
      } else if (progressIndicatorValue === 66) {
        baseProgressRef.current = 60
        remainingMsRef.current = 4 * 60 * 1000
      }

      baselineTimeRef.current = Date.now()
      setProgress(baseProgressRef.current)
    }

    const updateProgress = () => {
      const elapsed = Date.now() - baselineTimeRef.current
      const base = baseProgressRef.current
      const remainingMs = remainingMsRef.current

      if (!remainingMs || base >= 100) return

      const next = base + (elapsed / remainingMs) * (100 - base)

      setProgress(Math.min(next, 97))
    }

    updateProgress()
    const id = setInterval(updateProgress, 1000)

    return () => clearInterval(id)
  }, [getProgressValueAndStatusApiCallCount().progressIndicatorValue, showProgress])

  return progress
}
